// src/pages/CmsUserDetail.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "../../../utils/cmsAxios";
import TokenAllModal from "./CmsTokenAllModal"
import PaymentsModal from "./CmsPaymentsModal"
import StopModal from "./StopModal";
import DeleteModal from "./DeleteModal";
import UserStateModal, { subscriptionLabel } from "./UserStateModal";

const PROVIDER_LABELS = { kakao: "카카오톡", google: "구글", naver: "네이버", apple: "애플", local: "로컬" };
const PLATFORM_LABELS  = { android: "안드로이드", ios: "iOS" };

const CmsUsersDetail = () => {
  const navigate = useNavigate();
  const { user_id: routeUserId } = useParams();
  // const [sp] = useSearchParams();
  const [sp, setSp] = useSearchParams();
  const userId = Number(routeUserId || sp.get("user_id"));

  const [detail, setDetail] = useState(null);
  const [business, setBusiness] = useState(null);
  const [payments, setPayments] = useState([]);
  const [openPay, setOpenPay] = useState(false);
  const [stopReason, setStopReason] = useState("");
  const [, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [registerTag, setRegisterTag] = useState("");
  const [marketingAgree, setMarketingAgree] = useState(false);
  const [agreeAt, setAgreeAt] = useState({ marketingAt: null });
  const [saving, setSaving] = useState(false);

  // 모달 상태
  const [stopOpen, setStopOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "-";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${y}.${m}.${dd} ${hh}:${mm}`;
  }, []);

  const fetchDetail = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setErr("");
      const base = (process.env.REACT_APP_FASTAPI_ADS_URL || "").replace(/\/+$/, "");
      const { data } = await axios.get(`${base}/cms/get/user/detail`, { params: { user_id: userId } });
      // const d = data?.detail ?? {};
      // const pays = Array.isArray(data?.payments) ? data.payments : [];
      // setDetail(d);
      // setPayments(pays);
      const { detail, business, payments } = data ?? {};
      setDetail(detail ?? {});
      setBusiness(business ?? {});
      setPayments(Array.isArray(payments) ? payments : []);
      setStopReason(detail?.stop_reason ?? "");
    } catch (e) {
      console.error(e);
      setErr("상세 정보를 불러오지 못했습니다.");
      setDetail(null);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // const memberStatus = useMemo(() => "일반 회원", []);
  const channelLabel = useMemo(() => {
    const k = String(detail?.login_provider || "").toLowerCase();
    return PROVIDER_LABELS[k] ?? (detail?.login_provider || "-");
  }, [detail]);

  const fmtKRW = (n) =>
  (n === null || n === undefined) ? "-" : `${Number(n).toLocaleString("ko-KR")} 원`;

  const userCode = useMemo(() => {
    const id = detail?.user_id;
    if (detail?.user_code) return detail.user_code;
    return id != null ? `U-${String(id).padStart(7, "0")}` : "-";
  }, [detail]);

  const basicRows = useMemo(() => ([
    ["회원 번호", userCode],
    ["채널", channelLabel, "소셜 계정", detail?.email || "-"],
    ["표시 이름(닉네임)", detail?.nickname || "-", "회원상태", detail?.status === "stop" ? "정지 회원" : detail?.status === "deleted" ? "탈퇴 회원" : "일반 회원"],
    ["휴대폰 번호", detail?.phone || "-", "휴대폰 기종", PLATFORM_LABELS[String(detail?.platform || "").toLowerCase()] ?? (detail?.platform || "-")],
    ["가입 일시", formatDate(detail?.created_at), "최종 로그인 일시", formatDate(detail?.last_seen)],
  ]), [userCode, channelLabel, detail, formatDate]);


  const saveMarketingConsent = async (yes) => {
    const payload = { user_id: userId, agree: yes };
    await axios.post(`${process.env.REACT_APP_FASTAPI_ADS_URL}/cms/user/marketing_agree`, payload,
      {headers: { "Content-Type": "application/json" }}
    )
  };

  const onMarketingChange = async (e) => {
    const yes = e.target.value === "yes";
    if (!Number.isFinite(userId) || saving) return;

    const prevAgree = marketingAgree;
    const prevAt = agreeAt.marketingAt;

    setMarketingAgree(yes);
    setAgreeAt((s) => ({ ...s, marketingAt: yes ? new Date().toISOString() : s.marketingAt }));

    setSaving(true);
    try {
      await saveMarketingConsent(yes);
      setMarketingAgree(yes);
      setAgreeAt((s) => ({ ...s, marketingAt: yes ? new Date().toISOString() : null }));
    } catch (err) {
      setMarketingAgree(prevAgree);
      setAgreeAt({ marketingAt: prevAt });
      console.error("마케팅 동의 저장 실패", err);
    } finally {
      setSaving(false);
    }
  };

  //결제 모달
  const openPayModal = useCallback(() => {
  const next = new URLSearchParams(sp);
    next.set("modal", "payments");
    setSp(next, { replace: false });
    setOpenPay(true);
  }, [sp, setSp]);

  const closePayModal = useCallback(() => {
    const next = new URLSearchParams(sp);
    next.delete("modal");
    setSp(next, { replace: false });
    setOpenPay(false);
  }, [sp, setSp]);

  useEffect(() => {
    if (sp.get("modal") === "payments") setOpenPay(true);
  }, [sp]);
    

  // 토큰 사용
  const [allRows, setAllRows] = useState([]);   // 서버에서 받은 전체
  const [openAll, setOpenAll] = useState(false);

  const openModal = useCallback(() => {
    const next = new URLSearchParams(sp);
    next.set("modal", "token-usage");
    setSp(next, { replace: false });
    setOpenAll(true);
  }, [sp, setSp]);

  const closeModal = useCallback(() => {
    const next = new URLSearchParams(sp);
    next.delete("modal");
    next.delete("page");
    next.delete("date");
    setSp(next, { replace: false });
    setOpenAll(false);
  }, [sp, setSp]);

  useEffect(() => {
    if (sp.get("modal") === "token-usage") setOpenAll(true);
  }, [sp]);

  const onlyDate = (s) => (s ? String(s).slice(0, 10) : "-");
  const recentPayments = payments.slice(0, 5);

  // 서버 응답 -> 테이블 행으로 변환
  const normalize = (arr = []) =>
    arr.map((r, i) => {
      const remaining =
        Number(r.remaining_onetime || 0) + Number(r.remaining_subscription || 0);
      const amountLabel =
        Number(r.deducted || 0) > 0
          ? `차감 ${Number(r.deducted)}`
          : Number(r.granted || 0) > 0
          ? `지급 ${Number(r.granted)}`
          : "-";
      return {
        key: `${r.grant_date}-${i}`,
        used_at: r.grant_date,          // 백엔드가 일자만 주면 그대로
        amount_label: amountLabel,      // "차감 1"/"지급 3"
        description: r.description || "이미지 생성", // 있으면 교체
        remaining,                      // 잔여토큰 (원타임+구독)
      };
    });

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_FASTAPI_ADS_URL}/ticket/token/history`, { params: { user_id: userId, all: 1 } });
        if (data?.success) {
          setAllRows(normalize(data.history || []));
        } else {
          setAllRows([]);
        }
      } catch {
        setAllRows([]);
      }
    })();
  }, [userId]);

  const recent5 = allRows.slice(0, 5);


  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-[1280px] bg-white rounded-2xl shadow overflow-hidden">
        <div className="flex">
          <div className="flex-1 min-w-0 bg-neutral-50/70">
            <main className="pt-12 flex flex-col p-4 overflow-x-hidden w-full">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">회원정보 &gt; 상세(수정)</h2>
              </div>

              {err && <div className="mb-4 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{err}</div>}

              <h2 className="pb-3 font-bold">기본 정보</h2>
              <div className="border-gray-200 bg-white p-4">
                <table className="w-full table-fixed border-collapse text-sm">
                  <colgroup>
                    <col className="w-30" /><col />
                    <col className="w-30" /><col />
                  </colgroup>
                  <tbody>
                    {basicRows.map((row, idx) => {
                      const [lh, lv, rh, rv] = row;
                      if (row.length <= 2 || (!rh && !rv)) {
                        return (
                          <tr key={idx} className="border-b last:border-b-0">
                            <th className="bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">{lh}</th>
                            <td className="px-3 py-2 text-gray-900" colSpan={3}>{lv}</td>
                          </tr>
                        );
                      }
                      return (      
                        <tr key={idx} className="border-b last:border-b-0">
                          <th className="bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">{lh}</th>
                          <td className="px-3 py-2 text-gray-900">{lv}</td>
                          <th className="bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">{rh}</th>
                          <td className="px-3 py-2 text-gray-900">{rv}</td>
                        </tr>
                      );
                    })}
                    <tr>
                      <th className="bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">메모</th>
                      <td colSpan={3} className="px-3 py-2">
                        <input
                          type="text"
                          value={stopReason}
                          onChange={(e) => setStopReason(e.target.value)}  // onChangeMemo 대체
                          className="w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-gray-300"
                          placeholder="회원에 대한 메모 (정지 사유)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <section className="py-4">
                <h3 className="text-base font-semibold pb-3">업종 정보</h3>
                <div className="bg-white p-3">
                  <table className="w-full table-fixed border-collapse text-sm">
                    <colgroup>
                      <col className="w-28" /><col />
                      <col className="w-28" /><col />
                    </colgroup>
                    <tbody>
                      <tr className="border-b">
                        <th className="bg-gray-50 px-3 py-2 text-left text-gray-600">가게 이름</th>
                        <td className="px-3 py-2" colSpan={3}>{detail?.store_name || "-"}</td>
                      </tr>

                      <tr className="border-b">
                        <th className="bg-gray-50 px-3 py-2 text-left text-gray-600">주소</th>
                        <td className="px-3 py-2" colSpan={3}>{detail?.road_name_address || "-"}</td>
                      </tr>

                      <tr className="border-b">
                        <th className="bg-gray-50 px-3 py-2 text-left text-gray-600">대표자</th>
                        <td className="px-3 py-2">{business?.business_name || "-"}</td>
                        <th className="bg-gray-50 px-3 py-2 text-left text-gray-600">사업자등록번호</th>
                        <td className="px-3 py-2">{business?.business_number || "-"}</td>
                      </tr>
                      
                      <tr className="border-b">
                        <th className="bg-gray-50 px-3 py-2 text-left text-gray-600">업종</th>
                          <td className="px-3 py-2" colSpan={3}>{detail?.large_category_name || "-"} <span className="px-3">|</span> {detail?.medium_category_name || "-"}  <span className="px-3">|</span> {detail?.small_category_name || "-"}</td>
                      </tr>
                      <tr className="border-b">
                        <th className="bg-gray-50 px-3 py-2 text-left text-gray-600">세부 업종</th>
                        <td className="px-3 py-2" colSpan={3}>
                          <input
                            type="text"
                            value={registerTag ?? detail?.register_tag}
                            onChange={(e) => setRegisterTag(e.target.value)}
                            className="w-full h-9 rounded-md border border-neutral-300 bg-neutral-50 px-3 text-sm"
                            placeholder="세부 업종"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="py-4">
                <div className="flex items-center justify-between px-4 py-3">
                  <h3 className="text-base font-semibold">결제 내역</h3>                             
                    <button
                      type="button"
                      onClick={openPayModal}
                      className="text-sm text-neutral-700 hover:text-violet-600 inline-flex items-center gap-1"
                      title="결제 내역 전체 보기"
                    >
                      결제 관리 <span aria-hidden>↗</span>
                    </button>
                </div>                
                <div className="bg-white p-3">
                  <table className="w-full table-fixed text-sm">
                    <colgroup>
                      <col className="w-28"/>
                      <col className="w-28"/>
                      <col className="w-28"/>
                      <col className="w-28"/>
                      <col className="w-28"/>
                      <col className="w-36"/>
                    </colgroup>
                    <thead className="bg-neutral-50 text-neutral-500">
                      <tr>
                        <th className="px-3 py-2 text-center">구독 상태</th>
                        <th className="px-3 py-2 text-left">상품명</th>
                        <th className="px-3 py-2 text-center">스토어</th>
                        <th className="px-3 py-2 text-right">결제 금액</th>
                        <th className="px-3 py-2 text-center">구독 시작일</th>
                        <th className="px-3 py-2 text-center">다음 갱신일</th>
                        {/* <th className="px-3 py-2 text-left">트랜잭션 ID</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {/* <tr className="border-b">
                        <td className="px-3 py-2 text-center">구독중</td>
                        <td className="px-3 py-2">{detail?.ticket_name ?? "-"}</td>
                        <td className="px-3 py-2 text-center">Google play</td>
                        <td className="px-3 py-2 text-right">{fmtKRW(detail?.ticket_price ?? null)}</td>
                        <td className="px-3 py-2 text-center">{onlyDate(detail?.payment_date)}</td>
                        <td className="px-3 py-2 text-center">{onlyDate(detail?.next_renewal) ?? "기간 없음"}</td>
                      </tr> */}
                      {recentPayments.length ? (
                        recentPayments.map((p) => (
                          <tr key={p.payment_id} className="border-b">
                            <td className="px-3 py-2 text-center">{subscriptionLabel(detail.subscription_type)}</td>
                            <td className="px-3 py-2">{p.ticket_name || "-"}</td>
                            <td className="px-3 py-2 text-center">{p.store || "Google play"}</td>
                            <td className="px-3 py-2 text-right">{fmtKRW(p.ticket_price)}</td>
                            <td className="px-3 py-2 text-center">{onlyDate(p.payment_date)}</td>
                            <td className="px-3 py-2 text-center">{onlyDate(p.next_renewal) || "기간 없음"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-3 py-8 text-center text-neutral-500">
                            결제 내역이 없습니다.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <PaymentsModal
                  open={openPay}
                  onClose={closePayModal}
                  rows={payments}
                  pageSize={10}
                  fmtKRW={fmtKRW}
                  onlyDate={onlyDate}
                />
              </section>

              <section className="py-4">
                <div className="flex items-center justify-between px-4 py-3">
                  <h3 className="text-base font-semibold">토큰 사용 내역</h3>                             
                    <button
                      type="button"
                      onClick={openModal}
                      className="text-sm text-neutral-700 hover:text-violet-600 inline-flex items-center gap-1"
                      title="토큰 사용 내역 전체 보기"
                    >
                      전체 보기 <span aria-hidden>↗</span>
                    </button>
                </div>
                <div className="bg-white p-3">
                  <table className="w-full table-fixed text-sm">
                    <colgroup>
                      <col className="w-28"/>
                      <col className="w-28"/>
                      <col className="w-28"/>
                    </colgroup>
                    <thead className="bg-neutral-50 text-neutral-500">
                      <tr>
                        <th className="px-3 py-2 text-center">사용일시</th>
                        <th className="px-3 py-2 text-center">사용량</th>
                        <th className="px-3 py-2 text-center">잔여토큰</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent5.length ? (
                        recent5.map((it) => (
                          <tr key={it.key} className="border-b text-center">
                            <td className="px-3 py-2">{onlyDate(it.used_at)}</td>
                            <td className="px-3 py-2">{it.amount_label}</td>
                            <td className="px-3 py-2">{it.remaining}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="px-3 py-8 text-center text-neutral-500">
                            사용 내역이 없습니다.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <TokenAllModal
                  open={openAll}
                  onClose={closeModal}
                  rows={allRows}
                  pageSize={10}
                />
              </section>

              <section className="p-4">
                <h3 className="text-base font-semibold mb-3">개인정보 수집 및 이용 선택 동의 내역</h3>
                <div className="bg-white p-4">
                  <table className="w-full table-fixed border-collapse text-sm">
                    <colgroup>
                      <col className="w-40" /><col />
                      <col className="w-40" /><col />
                    </colgroup>
                    <tbody>
                      <tr className="border-b last:border-b-0">
                        <th className="bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">서비스 이용약관</th>
                        <td className="px-3 py-2">
                          동의 <span className="text-neutral-500 ml-2">({formatDate(detail?.created_at)})</span>
                        </td>
                        <th className="bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">개인정보 수집 및 이용</th>
                        <td className="px-3 py-2">
                          동의 <span className="text-neutral-500 ml-2">({formatDate(detail?.created_at)})</span>
                        </td>
                      </tr>

                      <tr>
                        <th className="bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">만 14세 이상</th>
                        <td className="px-3 py-2">
                          동의 <span className="text-neutral-500 ml-2">({formatDate(detail?.created_at)})</span>
                        </td>
                        <th className="bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">마케팅 정보 수신</th>
                        <td className="px-3 py-2">
                          <div className="inline-flex items-center gap-2">
                            <select
                              className="h-9 rounded-md border border-neutral-300 bg-white px-3 text-sm"
                              value={marketingAgree ? "yes" : "no"}
                              onChange={onMarketingChange}
                              disabled={saving}
                            >
                              <option value="no">미동의</option>
                              <option value="yes">동의</option>
                            </select>
                            <p className="text-neutral-500 ml-2">
                              {marketingAgree && agreeAt.marketingAt
                                ? `(${formatDate(agreeAt.marketingAt)})`
                                : '동의 이력 없음'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <div className="flex justify-end gap-2 px-4 mt-4">
                <button
                  type="button"
                  onClick={() => setStopOpen(true)}
                  className="h-9 px-4 rounded-md border text-sm bg-neutral-700 text-white hover:bg-neutral-600"
                >
                  회원 정지
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteOpen(true)}
                  className="h-9 px-4 rounded-md border text-sm bg-red-600 text-white hover:bg-red-500"
                >
                  삭제
                </button>
              </div>
              
              <div className="flex gap-2 mx-auto py-6">                
                <button
                  type="button"
                  disabled
                  className="h-9 px-5 rounded-md bg-violet-500/40 text-white cursor-not-allowed"
                  title="수정 API 준비 중"
                >
                  저장
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/users`)}
                  className="h-9 px-4 rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-100"
                >
                  이전
                </button>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CmsUsersDetail;
